import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { ArrowRight, Laptop, Zap, Radio, Settings, Palette, Leaf, Trees, CheckCircle2, Award, Globe, Briefcase } from 'lucide-react';
import { programs } from '../data/programs';

const highlights = [
  { icon: <Award className="w-6 h-6" />, title: 'Diplôme reconnu', desc: 'Certification nationale et internationale' },
  { icon: <Briefcase className="w-6 h-6" />, title: 'Stage professionnel', desc: '3 à 6 mois en entreprise' },
  { icon: <Globe className="w-6 h-6" />, title: 'Mobilité', desc: "Possibilité de semestres à l'étranger" },
  { icon: <CheckCircle2 className="w-6 h-6" />, title: 'Coaching 7 étapes', desc: 'Accompagnement entrepreneurial complet' },
];

export default function BachelorPage() {
  const { t } = useTranslation();

  // Filter for bachelor-level programs or just use the first 7 as requested
  const bachelorList = programs.slice(0, 7);

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={t('bachelor_hero_title', 'Programmes de Licence')}
        description={t('bachelor_hero_subtitle', 'Des formations complètes de 3 ans (180 crédits) préparant les étudiants aux métiers de demain.')}
        breadcrumbs={[
          { label: t('nav_programs'), href: '/programs' },
          { label: t('bachelor_title', 'Licence'), href: '/programs/bachelor' }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-20 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Image Banner */}
          <div className="w-full h-48 md:h-64 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold">
            <img src="/images/campus/students-01.jpg" alt="Bachelor Students" className="w-full h-full object-cover object-center" />
          </div>

          {/* Programs Grid */}
          <section className="space-y-10">
            <div className="space-y-4">
              <span className="inline-block px-4 py-1.5 text-[10px] font-black text-navy border-2 border-gold rounded-full tracking-widest uppercase">
                {t('bachelor_programs_badge', 'Nos Licences')}
              </span>
              <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic">
                {t('bachelor_programs_title', 'Choisissez Votre Filière')}
              </h2>
              <p className="text-gray-500 font-medium">7 filières disponibles au niveau Bachelor (Licence Professionnelle)</p>
            </div>

            <div className="grid grid-cols-1 gap-5">
              {bachelorList.map((program) => (
                <div
                  key={program.slug}
                  className="group bg-gray-50 border border-gray-100 p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center hover:bg-white hover:shadow-2xl hover:border-gold transition-all duration-500 rounded-sm"
                >
                  <div className={`w-16 h-16 rounded-xl bg-navy/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
                    <div className="text-navy group-hover:text-gold transition-colors">
                       {program.slug === 'informatique' && <Laptop className="w-8 h-8" />}
                       {program.slug === 'electricite-energies' && <Zap className="w-8 h-8" />}
                       {program.slug === 'reseaux-telecoms' && <Radio className="w-8 h-8" />}
                       {program.slug === 'mecatronique' && <Settings className="w-8 h-8" />}
                       {program.slug === 'arts-numeriques' && <Palette className="w-8 h-8" />}
                       {program.slug === 'agroforesterie' && <Leaf className="w-8 h-8" />}
                       {program.slug === 'sciences-bois-foret' && <Trees className="w-8 h-8" />}
                    </div>
                  </div>
                  <div className="flex-1 text-center md:text-left space-y-2">
                    <h3 className="text-xl font-black text-navy group-hover:text-gold transition-colors uppercase tracking-tight leading-none">
                      {program.title}
                    </h3>
                    <p className="text-gray-500 font-medium leading-relaxed text-sm line-clamp-2">{program.description}</p>
                    <span className="inline-block text-[10px] font-black text-gold uppercase tracking-widest">{program.duration.split(' / ')[0]}</span>
                  </div>
                  <Link
                    to={`/programs/${program.slug}`}
                    className="bg-navy text-white p-4 rounded-full group-hover:bg-gold transition-all active:scale-95 flex-shrink-0"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              ))}
            </div>
          </section>

          {/* Highlights */}
          <section className="bg-navy p-10 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-y-1/2 -translate-x-1/2 group-hover:scale-110 transition-transform duration-1000 pointer-events-none"></div>
            <div className="relative z-10 space-y-12">
              <h2 className="text-3xl font-black uppercase tracking-tighter italic text-center">
                {t('bachelor_highlights_title', 'Pourquoi Choisir Nos Licences')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {highlights.map((h, idx) => (
                  <div key={idx} className="space-y-4 text-center">
                    <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center mx-auto border border-gold/30">
                      <div className="text-gold">{h.icon}</div>
                    </div>
                    <h4 className="font-black text-sm uppercase tracking-widest text-gold">{h.title}</h4>
                    <p className="text-white/60 text-xs font-medium">{h.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
